/* =========================================================
   Gleam theme — theme.js
   Vanilla JS, no dependencies. Custom elements + small utils.
   ========================================================= */
(() => {
  'use strict';
  const theme = window.theme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};

  /* ---------- Utils ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, wait = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || (theme.currencyCodeEnabled ? theme.moneyWithCurrencyFormat : theme.moneyFormat) || '{{amount}}';
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    const match = fmt.match(placeholderRegex);
    const type = match ? match[1] : 'amount';
    switch (type) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }
  window.themeFormatMoney = formatMoney;

  function toast(message, ms = 2400) {
    const region = $('#toast-region');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    region.appendChild(el);
    setTimeout(() => el.remove(), ms);
  }

  function fetchConfig(type = 'json') {
    return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: `application/${type}` } };
  }

  function getFocusable(el) {
    return $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el).filter(e => e.offsetParent !== null);
  }

  function trapFocus(container, first) {
    const els = getFocusable(container);
    const start = first || els[0];
    const end = els[els.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === start) { e.preventDefault(); end && end.focus(); }
      else if (!e.shiftKey && document.activeElement === end) { e.preventDefault(); start && start.focus(); }
    }
    container._trap = handler;
    document.addEventListener('keydown', handler);
    setTimeout(() => start && start.focus(), 50);
  }
  function releaseFocus(container) {
    if (container && container._trap) { document.removeEventListener('keydown', container._trap); container._trap = null; }
  }

  let scrollLockCount = 0;
  function lockScroll(lock) {
    scrollLockCount = Math.max(0, scrollLockCount + (lock ? 1 : -1));
    document.body.style.overflow = scrollLockCount > 0 ? 'hidden' : '';
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const els = $$('.reveal, .stat, .stock-bar');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion && false) { els.forEach(e => e.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(e => io.observe(e));
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const wrapper = $('.header-wrapper');
    if (!wrapper) return;
    const hideOnScroll = wrapper.dataset.hideOnScroll === 'true';
    let last = window.scrollY;
    const update = () => {
      const y = window.scrollY;
      wrapper.classList.toggle('is-scrolled', y > 8);
      if (hideOnScroll) {
        if (y > last && y > 240) wrapper.classList.add('is-hidden');
        else wrapper.classList.remove('is-hidden');
      }
      last = y;
    };
    on(window, 'scroll', update, { passive: true });
    update();
    // Desktop submenus: close on outside click
    on(document, 'click', (e) => {
      $$('.header__nav details[open]').forEach(d => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
    on(document, 'keydown', (e) => { if (e.key === 'Escape') $$('.header__nav details[open]').forEach(d => d.removeAttribute('open')); });
  }

  /* ---------- Generic drawer (menu) ---------- */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this);
      $$('[data-drawer-close]', this).forEach(b => on(b, 'click', () => this.close()));
      on($('.drawer__backdrop', this), 'click', () => this.close());
      on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      const id = this.id;
      $$(`[data-drawer-open="${id}"]`).forEach(b => on(b, 'click', (e) => { e.preventDefault(); this.open(b); }));
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      trapFocus(this.panel);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      releaseFocus(this.panel);
      this.opener && this.opener.focus();
    }
  }
  customElements.define('drawer-element', DrawerElement);

  /* ---------- Search modal ---------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      $$('[data-search-open]').forEach(b => on(b, 'click', (e) => { e.preventDefault(); this.open(b); }));
      $$('[data-search-close]', this).forEach(b => on(b, 'click', () => this.close()));
      on(this, 'click', (e) => { if (e.target === this) this.close(); });
      on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
    }
    open(opener) { this.opener = opener; this.classList.add('is-open'); lockScroll(true); setTimeout(() => this.input && this.input.focus(), 40); }
    close() { this.classList.remove('is-open'); lockScroll(false); this.opener && this.opener.focus(); }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------- Announcement bar rotation ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement-bar__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval || '5', 10) * 1000;
      $$('[data-ann-prev]', this).forEach(b => on(b, 'click', () => this.go(-1)));
      $$('[data-ann-next]', this).forEach(b => on(b, 'click', () => this.go(1)));
      if (this.dataset.autoplay === 'true' && !reducedMotion) this.start();
      on(this, 'mouseenter', () => this.stop());
      on(this, 'mouseleave', () => this.dataset.autoplay === 'true' && !reducedMotion && this.start());
    }
    start() { this.stop(); this.timer = setInterval(() => this.go(1), this.interval); }
    stop() { clearInterval(this.timer); }
    go(dir) {
      this.slides[this.index].classList.remove('is-active');
      this.index = (this.index + dir + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Countdown timer ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const mode = this.dataset.mode || (theme.countdown && theme.countdown.mode) || 'daily';
      if (mode === 'off') { this.hidden = true; return; }
      this.mode = mode;
      this.endRaw = this.dataset.end || (theme.countdown && theme.countdown.end) || '';
      this.showDays = this.dataset.days !== 'false';
      this.units = { d: $('[data-unit="d"]', this), h: $('[data-unit="h"]', this), m: $('[data-unit="m"]', this), s: $('[data-unit="s"]', this) };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    target() {
      const now = new Date();
      if (this.mode === 'fixed') {
        const t = new Date(this.endRaw.replace(' ', 'T'));
        return isNaN(t) ? null : t;
      }
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
      return end;
    }
    tick() {
      const t = this.target();
      if (!t) { this.hidden = true; clearInterval(this.timer); return; }
      let diff = Math.max(0, Math.floor((t - new Date()) / 1000));
      if (diff === 0 && this.mode === 'fixed') { this.classList.add('is-expired'); if (this.dataset.hideExpired === 'true') this.hidden = true; }
      const d = Math.floor(diff / 86400); diff -= d * 86400;
      const h = Math.floor(diff / 3600); diff -= h * 3600;
      const m = Math.floor(diff / 60); const s = diff - m * 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.units.d) { this.units.d.textContent = pad(d); const wrap = this.units.d.closest('.countdown__unit'); if (wrap) wrap.hidden = !(this.showDays && d > 0); }
      if (this.units.h) this.units.h.textContent = pad(this.showDays ? h : h + d * 24);
      if (this.units.m) this.units.m.textContent = pad(m);
      if (this.units.s) this.units.s.textContent = pad(s);
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Delivery estimate ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min || (theme.delivery && theme.delivery.min) || 5, 10);
      const max = parseInt(this.dataset.max || (theme.delivery && theme.delivery.max) || 10, 10);
      const fmt = (d) => d.toLocaleDateString(document.documentElement.lang || 'en', { weekday: 'short', day: 'numeric', month: 'short' });
      const addBiz = (days) => { const d = new Date(); let n = 0; while (n < days) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0) n++; } return d; };
      const s = $('[data-start]', this), e = $('[data-end]', this);
      if (s) s.textContent = fmt(addBiz(min));
      if (e) e.textContent = fmt(addBiz(max));
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Cart API ---------- */
  const Cart = {
    sectionIds() { return ['cart-drawer', 'cart-icon-bubble']; },
    async add(body) {
      const res = await fetch(routes.cartAdd || '/cart/add.js', { ...fetchConfig(), body: JSON.stringify({ ...body, sections: this.sectionIds().join(','), sections_url: window.location.pathname }) });
      const data = await res.json();
      if (!res.ok || data.status) throw data;
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    async change(payload) {
      const res = await fetch(routes.cartChange || '/cart/change.js', { ...fetchConfig(), body: JSON.stringify({ ...payload, sections: this.sectionIds().join(','), sections_url: window.location.pathname }) });
      const data = await res.json();
      if (!res.ok || data.status) throw data;
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    async update(payload) {
      const res = await fetch(routes.cartUpdate || '/cart/update.js', { ...fetchConfig(), body: JSON.stringify({ ...payload, sections: this.sectionIds().join(','), sections_url: window.location.pathname }) });
      const data = await res.json();
      if (!res.ok || data.status) throw data;
      this.render(data.sections);
      return data;
    },
    async refresh() {
      const res = await fetch(`${routes.root || '/'}?sections=${this.sectionIds().join(',')}`);
      const sections = await res.json();
      this.render(sections);
    },
    render(sections) {
      if (!sections) return;
      const parse = (html, sel) => { const d = new DOMParser().parseFromString(html, 'text/html'); return sel ? d.querySelector(sel) : d; };
      if (sections['cart-drawer']) {
        const drawer = $('cart-drawer');
        const fresh = parse(sections['cart-drawer'], '.cart-drawer__panel');
        if (drawer && fresh) { $('.cart-drawer__panel', drawer).innerHTML = fresh.innerHTML; drawer.bind(); }
        const pageItems = $('#MainCartItems');
        if (pageItems) { const p = parse(sections['cart-drawer'], '[data-cart-page-source]'); }
      }
      if (sections['cart-icon-bubble']) {
        $$('#cart-icon-bubble, [data-cart-bubble]').forEach(el => { const fresh = parse(sections['cart-icon-bubble'], '.shopify-section') || parse(sections['cart-icon-bubble'], 'body'); el.innerHTML = fresh ? fresh.innerHTML : el.innerHTML; });
      }
    }
  };
  window.themeCart = Cart;

  /* ---------- Cart drawer element ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.bind();
      on($('.cart-drawer__backdrop', this), 'click', () => this.close());
      on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      $$('[data-cart-open]').forEach(b => on(b, 'click', (e) => { if (theme.cartType !== 'drawer') return; e.preventDefault(); this.open(b); }));
    }
    bind() {
      this.panel = $('.cart-drawer__panel', this);
      $$('[data-cart-close]', this).forEach(b => on(b, 'click', () => this.close()));
      $$('[data-cart-remove]', this).forEach(b => on(b, 'click', (e) => { e.preventDefault(); this.changeLine(b.dataset.cartRemove, 0); }));
      $$('[data-cart-qty]', this).forEach(inp => {
        on(inp, 'change', () => this.changeLine(inp.dataset.cartQty, parseInt(inp.value, 10) || 0));
      });
      $$('[data-upsell-add]', this).forEach(b => on(b, 'click', async () => {
        b.classList.add('is-loading'); b.disabled = true;
        try { await Cart.add({ items: [{ id: parseInt(b.dataset.upsellAdd, 10), quantity: 1 }] }); }
        catch (err) { toast((err && err.description) || strings.cartError); b.classList.remove('is-loading'); b.disabled = false; }
      }));
      const note = $('[data-cart-note]', this);
      if (note) on(note, 'change', debounce(() => Cart.update({ note: note.value }), 300));
      const checkout = $('[name="checkout"]', this);
      if (checkout) on(checkout, 'click', () => checkout.classList.add('is-loading'));
    }
    async changeLine(key, quantity) {
      this.classList.add('is-loading');
      try { await Cart.change({ id: key, quantity }); }
      catch (err) { toast((err && (err.message || err.description)) || strings.cartError); await Cart.refresh(); }
      finally { this.classList.remove('is-loading'); }
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      trapFocus(this.panel, $('[data-cart-close]', this));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      releaseFocus(this.panel);
      this.opener && this.opener.focus && this.opener.focus();
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      $$('button', this).forEach(b => on(b, 'click', (e) => {
        e.preventDefault();
        const step = parseInt(this.input.step || '1', 10);
        const min = parseInt(this.input.min || '1', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        let v = parseInt(this.input.value, 10) || min;
        v = b.name === 'plus' ? v + step : v - step;
        v = Math.min(max, Math.max(min, v));
        this.input.value = v;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.button = $('[type="submit"]', this.form);
      this.error = $('.product-form__error', this);
      on(this.form, 'submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      this.setError('');
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-disabled', 'true');
      const fd = new FormData(this.form);
      const body = { items: [{ id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10), properties: {} }] };
      for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) body.items[0].properties[m[1]] = v; }
      if (!Object.keys(body.items[0].properties).length) delete body.items[0].properties;
      try {
        await Cart.add(body);
        const drawer = $('cart-drawer');
        if (theme.cartType === 'drawer' && drawer) drawer.open(this.button);
        else window.location.href = routes.cart || '/cart';
        const label = $('.btn__label', this.button);
        if (label) { const orig = label.textContent; label.textContent = strings.added || 'Added'; setTimeout(() => label.textContent = orig, 1600); }
      } catch (err) {
        this.setError((err && (err.description || err.message)) || strings.cartError);
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
      }
    }
    setError(msg) { if (!this.error) return; this.error.hidden = !msg; const t = $('span', this.error); if (t) t.textContent = msg; }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const script = $('[data-variants-json]', this) || $('[data-variants-json]', this.closest('[data-product-root]') || document);
      try { this.variants = JSON.parse(script.textContent); } catch (e) { this.variants = []; }
      this.root = this.closest('[data-product-root]') || document;
      this.updateUrl = this.dataset.updateUrl !== 'false';
      on(this, 'change', () => this.onChange());
      this.onChange(true);
    }
    updateOptionLabels() {
      $$('fieldset', this).forEach(fs => {
        const checked = $('input:checked', fs);
        const label = $('[data-option-value]', fs);
        if (checked && label) label.textContent = checked.value;
      });
    }
    selectedOptions() {
      const fieldsets = $$('fieldset', this);
      if (fieldsets.length) return fieldsets.map(fs => { const c = $('input:checked', fs); return c ? c.value : null; });
      return $$('select', this).map(s => s.value);
    }
    onChange(initial) {
      const opts = this.selectedOptions();
      this.updateOptionLabels();
      const variant = this.variants.find(v => v.options.every((o, i) => o === opts[i]));
      this.currentVariant = variant;
      this.markAvailability(opts);
      if (!variant) { this.setButton(false, strings.unavailable); return; }
      $$('input[name="id"]', this.root).forEach(i => { i.value = variant.id; i.dispatchEvent(new Event('change', { bubbles: false })); });
      this.renderPrice(variant);
      this.setButton(variant.available, variant.available ? null : strings.soldOut);
      if (variant.sku !== undefined) $$('[data-sku]', this.root).forEach(e => { e.textContent = variant.sku || ''; e.closest('[data-sku-wrap]') && (e.closest('[data-sku-wrap]').hidden = !variant.sku); });
      if (this.updateUrl && !initial && window.history.replaceState) { const url = new URL(window.location); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url); }
      $$('[data-stock-for]', this.root).forEach(e => { e.hidden = String(e.dataset.stockFor) !== String(variant.id); });
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, root: this.root, initial: !!initial } }));
    }
    markAvailability(opts) {
      const fieldsets = $$('fieldset', this);
      fieldsets.forEach((fs, idx) => {
        $$('input', fs).forEach(input => {
          const test = opts.slice(); test[idx] = input.value;
          const match = this.variants.find(v => v.options.every((o, i) => i === idx ? o === input.value : (test[i] == null || o === test[i])));
          const label = $(`label[for="${input.id}"]`, fs);
          if (label) label.classList.toggle('is-unavailable', !(match && match.available));
        });
      });
    }
    renderPrice(variant) {
      $$('[data-price-root]', this.root).forEach(root => {
        const sale = $('[data-price-sale]', root), cmp = $('[data-price-compare]', root), badge = $('[data-price-badge]', root), unit = $('[data-price-unit]', root);
        if (sale) sale.textContent = formatMoney(variant.price);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        root.classList.toggle('price--on-sale', !!onSale);
        if (cmp) { cmp.hidden = !onSale; if (onSale) cmp.textContent = formatMoney(variant.compare_at_price); }
        if (badge) { badge.hidden = !onSale; if (onSale) { const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100); badge.textContent = badge.dataset.template ? badge.dataset.template.replace('[percent]', pct) : `Save ${pct}%`; } }
        if (unit) { if (variant.unit_price) { unit.hidden = false; unit.textContent = `${formatMoney(variant.unit_price)} / ${variant.unit_price_measurement && variant.unit_price_measurement.reference_unit || ''}`; } else unit.hidden = true; }
      });
    }
    setButton(available, text) {
      $$('product-form [type="submit"]', this.root).forEach(btn => {
        const label = $('.btn__label', btn) || btn;
        if (!btn.dataset.defaultLabel) btn.dataset.defaultLabel = label.textContent.trim();
        label.textContent = text || btn.dataset.defaultLabel;
        if (available) { btn.removeAttribute('disabled'); btn.removeAttribute('aria-disabled'); }
        else { btn.setAttribute('disabled', 'disabled'); btn.setAttribute('aria-disabled', 'true'); }
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Bundle selector ---------- */
  class BundleSelector extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      this.mode = this.dataset.mode || 'quantity';
      this.tiers = $$('.bundle__tier', this);
      this.basePrice = parseInt(this.dataset.price, 10) || 0;
      this.baseCompare = parseInt(this.dataset.compare, 10) || 0;
      this.qtyInput = $('input[name="quantity"]', this.root);
      this.tiers.forEach(t => {
        const input = $('input', t);
        on(input, 'change', () => this.select(t));
        on(t, 'click', (e) => { if (e.target !== input) { input.checked = true; this.select(t); } });
      });
      on(document, 'variant:change', (e) => {
        if (e.detail.root !== this.root) return;
        const v = e.detail.variant;
        if (this.mode === 'quantity') { this.basePrice = v.price; this.baseCompare = v.compare_at_price || 0; this.renderPrices(); }
      });
      const preselected = this.tiers.find(t => $('input', t).checked) || this.tiers[0];
      if (preselected) { $('input', preselected).checked = true; this.select(preselected, true); }
      this.renderPrices();
    }
    select(tier, initial) {
      this.tiers.forEach(t => t.classList.toggle('is-selected', t === tier));
      const qty = parseInt(tier.dataset.qty, 10) || 1;
      if (this.mode === 'quantity') {
        if (this.qtyInput) { this.qtyInput.value = qty; this.qtyInput.dispatchEvent(new Event('change', { bubbles: true })); }
      } else if (tier.dataset.variantValue) {
        const picker = $('variant-picker', this.root);
        if (picker) {
          const input = $$('input', picker).find(i => i.value === tier.dataset.variantValue) || $$('select option', picker).find(o => o.value === tier.dataset.variantValue);
          if (input) { if (input.tagName === 'OPTION') { input.parentElement.value = input.value; } else input.checked = true; picker.onChange(); }
        }
      }
      const gift = $('.bundle__gift', this);
      if (gift) gift.hidden = !(tier.dataset.gift === 'true');
      this.renderPrices();
      document.dispatchEvent(new CustomEvent('bundle:change', { detail: { qty, tier, root: this.root } }));
    }
    renderPrices() {
      if (this.mode !== 'quantity') return;
      const selected = this.tiers.find(t => t.classList.contains('is-selected'));
      const showEach = this.dataset.showEach !== 'false';
      const priceFor = (t) => {
        const qty = parseInt(t.dataset.qty, 10) || 1;
        const disc = parseFloat(t.dataset.discount || '0') / 100;
        const full = this.basePrice * qty;
        const fixed = t.dataset.fixed ? parseInt(t.dataset.fixed, 10) : NaN;
        const total = !isNaN(fixed) ? fixed : Math.round(full * (1 - disc));
        const compare = Math.max(full, (this.baseCompare || 0) * qty);
        return { qty, total, compare };
      };
      this.tiers.forEach(t => {
        const { qty, total, compare } = priceFor(t);
        const totalEl = $('[data-tier-total]', t), cmpEl = $('[data-tier-compare]', t), eachEl = $('[data-tier-each]', t);
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (cmpEl) { const show = compare > total; cmpEl.hidden = !show; if (show) cmpEl.textContent = formatMoney(compare); }
        if (eachEl) { eachEl.textContent = (strings.each || '[price] each').replace('[price]', formatMoney(Math.round(total / qty))); eachEl.hidden = qty < 2 || !showEach; }
      });
      if (selected) {
        const { total, compare } = priceFor(selected);
        $$('[data-bundle-total]', this.root).forEach(e => e.textContent = formatMoney(total));
        $$('[data-bundle-compare]', this.root).forEach(e => { e.hidden = compare <= total; e.textContent = formatMoney(compare); });
        $$('[data-bundle-save]', this.root).forEach(e => { const s = compare - total; e.hidden = s <= 0; e.textContent = (strings.youSave || 'You save [amount]').replace('[amount]', formatMoney(s)); });
      }
    }
  }
  customElements.define('bundle-selector', BundleSelector);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('.gallery__track', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('.gallery__thumb', this);
      this.dots = $$('.gallery__dots span', this);
      this.root = this.closest('[data-product-root]') || document;
      this.index = 0;
      this.thumbs.forEach((t, i) => on(t, 'click', () => this.go(i)));
      on($('.gallery__nav--prev', this), 'click', () => this.go(this.index - 1));
      on($('.gallery__nav--next', this), 'click', () => this.go(this.index + 1));
      on(this.track, 'scroll', debounce(() => { const i = Math.round(this.track.scrollLeft / this.track.clientWidth); if (i !== this.index) this.setActive(i); }, 80), { passive: true });
      on(document, 'variant:change', (e) => {
        if (e.detail.root !== this.root || !e.detail.variant.featured_media) return;
        const id = String(e.detail.variant.featured_media.id);
        const i = this.slides.findIndex(s => s.dataset.mediaId === id);
        if (i > -1) this.go(i);
      });
      $$('video', this).forEach(v => { on(v, 'play', () => this.pauseOthers(v)); });
    }
    go(i) {
      if (!this.slides.length) return;
      i = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.track.clientWidth * i, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, k) => { t.classList.toggle('is-active', k === i); if (k === i && t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      $$('video', this).forEach(v => { if (!this.slides[i].contains(v)) v.pause(); });
    }
    pauseOthers(current) { $$('video', this).forEach(v => { if (v !== current) v.pause(); }); }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      const target = $('[data-sticky-anchor]', this.root) || $('product-form', this.root);
      if (!target) return;
      const btn = $('button', this);
      on(btn, 'click', () => { const form = $('product-form form', this.root); if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); });
      const io = new IntersectionObserver((entries) => { entries.forEach(en => this.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)); }, { threshold: 0 });
      io.observe(target);
      on(document, 'variant:change', (e) => {
        if (e.detail.root !== this.root) return;
        const v = e.detail.variant;
        const p = $('[data-sticky-price]', this); if (p) p.textContent = formatMoney(v.price);
        const c = $('[data-sticky-compare]', this); if (c) { const show = v.compare_at_price > v.price; c.hidden = !show; if (show) c.textContent = formatMoney(v.compare_at_price); }
        btn.disabled = !v.available; $('.btn__label', btn).textContent = v.available ? btn.dataset.label : strings.soldOut;
      });
      on(document, 'bundle:change', (e) => { if (e.detail.root !== this.root) return; const t = $('[data-bundle-total]', this.root); const p = $('[data-sticky-price]', this); if (t && p) p.textContent = t.textContent; });
      on(document, 'cart:updated', () => { const d = $('cart-drawer'); if (d && d.classList.contains('is-open')) this.classList.remove('is-visible'); });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Before / after slider ---------- */
  class CompareSlider extends HTMLElement {
    connectedCallback() {
      const range = $('input[type="range"]', this);
      if (!range) return;
      const set = (v) => this.style.setProperty('--pos', `${v}%`);
      on(range, 'input', () => set(range.value));
      set(range.value);
    }
  }
  customElements.define('compare-slider', CompareSlider);

  /* ---------- Carousel (reviews etc.) ---------- */
  class CarouselComponent extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      if (!this.track) return;
      on($('[data-carousel-prev]', this), 'click', () => this.scrollBy(-1));
      on($('[data-carousel-next]', this), 'click', () => this.scrollBy(1));
      if (this.dataset.autoplay === 'true' && !reducedMotion) {
        this.timer = setInterval(() => { if (!this.matches(':hover')) this.scrollBy(1, true); }, parseInt(this.dataset.interval || '5', 10) * 1000);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    scrollBy(dir, loop) {
      const item = this.track.firstElementChild;
      const w = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 16) : this.track.clientWidth;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (loop && dir > 0 && this.track.scrollLeft >= max - 4) { this.track.scrollTo({ left: 0, behavior: 'smooth' }); return; }
      this.track.scrollBy({ left: w * dir, behavior: 'smooth' });
    }
  }
  customElements.define('carousel-component', CarouselComponent);

  /* ---------- Share button ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const btn = $('button', this);
      const url = this.dataset.url || window.location.href;
      const title = this.dataset.title || document.title;
      on(btn, 'click', async () => {
        if (navigator.share) { try { await navigator.share({ title, url }); } catch (e) {} }
        else { try { await navigator.clipboard.writeText(url); toast(strings.copied || 'Link copied'); } catch (e) {} }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Quick add (product cards, single-variant) ---------- */
  function initQuickAdd() {
    $$('form[data-quick-add]').forEach(form => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const btn = $('[type="submit"]', form);
        btn.classList.add('is-loading'); btn.disabled = true;
        try {
          await Cart.add({ items: [{ id: parseInt($('[name="id"]', form).value, 10), quantity: 1 }] });
          const drawer = $('cart-drawer');
          if (theme.cartType === 'drawer' && drawer) drawer.open(btn); else window.location.href = routes.cart || '/cart';
        } catch (err) { toast((err && err.description) || strings.cartError); }
        finally { btn.classList.remove('is-loading'); btn.disabled = false; }
      });
    });
  }

  /* ---------- Video embeds (click to play) ---------- */
  function initVideos() {
    $$('[data-video-poster]').forEach(wrap => {
      on($('[data-video-play]', wrap), 'click', () => {
        const src = wrap.dataset.videoSrc;
        const type = wrap.dataset.videoType;
        const holder = $('[data-video-holder]', wrap);
        if (!src || !holder) return;
        if (type === 'youtube') holder.innerHTML = `<iframe src="https://www.youtube.com/embed/${src}?autoplay=1&rel=0&playsinline=1" title="Video" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
        else if (type === 'vimeo') holder.innerHTML = `<iframe src="https://player.vimeo.com/video/${src}?autoplay=1" title="Video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
        else { const v = $('video', holder); if (v) { v.controls = true; v.play(); } }
        wrap.classList.add('is-playing');
        $('[data-video-play]', wrap).hidden = true;
        const poster = $('[data-video-poster-img]', wrap); if (poster) poster.hidden = true;
      });
    });
  }

  /* ---------- Cart page (non-drawer) ---------- */
  function initCartPage() {
    const page = $('[data-cart-page]');
    if (!page) return;
    const reload = () => window.location.reload();
    $$('[data-cart-qty]', page).forEach(inp => on(inp, 'change', async () => { try { await Cart.change({ id: inp.dataset.cartQty, quantity: parseInt(inp.value, 10) || 0 }); reload(); } catch (e) { toast(e.description || strings.cartError); } }));
    $$('[data-cart-remove]', page).forEach(b => on(b, 'click', async (e) => { e.preventDefault(); try { await Cart.change({ id: b.dataset.cartRemove, quantity: 0 }); reload(); } catch (err) { toast(strings.cartError); } }));
  }

  /* ---------- Customer addresses ---------- */
  function initAddresses() {
    const root = $('[data-customer-addresses]');
    if (!root) return;
    $$('[data-address-toggle]', root).forEach(b => on(b, 'click', () => { const f = $(b.dataset.addressToggle); if (f) f.classList.toggle('is-open'); }));
    $$('[data-address-delete]', root).forEach(b => on(b, 'click', () => {
      if (!confirm(b.dataset.confirm)) return;
      const f = document.createElement('form'); f.method = 'post'; f.action = b.dataset.target;
      f.innerHTML = '<input type="hidden" name="_method" value="delete"><input type="hidden" name="form_type" value="customer_address">';
      document.body.appendChild(f); f.submit();
    }));
    $$('[data-country-select]', root).forEach(country => {
      const province = $(country.dataset.provinceTarget) || $(`#${country.dataset.provinceId}`);
      const wrap = province && province.closest('[data-province-wrap]');
      const update = () => {
        if (!province) return;
        const opt = country.options[country.selectedIndex];
        let provinces = [];
        try { provinces = JSON.parse(opt.dataset.provinces || '[]'); } catch (e) {}
        province.innerHTML = provinces.map(p => `<option value="${p[0]}">${p[1]}</option>`).join('');
        if (wrap) wrap.hidden = !provinces.length;
      };
      on(country, 'change', update);
      const def = country.dataset.default; if (def) country.value = def;
      update();
      const pdef = province && province.dataset.default; if (pdef) province.value = pdef;
    });
  }

  /* ---------- Localization form ---------- */
  function initLocalization() {
    $$('form[data-localization]').forEach(form => $$('select', form).forEach(s => on(s, 'change', () => form.submit())));
  }

  /* ---------- Init ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    initQuickAdd();
    initVideos();
    initCartPage();
    initAddresses();
    initLocalization();
  });

  // Theme editor support
  document.addEventListener('shopify:section:load', () => { initReveal(); initQuickAdd(); initVideos(); });
})();

/* ---------- Product recommendations (Section Rendering API) ---------- */
(() => {
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      const load = () => fetch(url).then(r => r.text()).then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; this.classList.add('is-loaded'); }
        else this.closest('.shopify-section') && this.closest('.shopify-section').classList.add('shopify-section--hidden');
      }).catch(() => {});
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) { load(); io.disconnect(); } }, { rootMargin: '400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
})();

/* ---------- Promo popup ---------- */
(() => {
  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key || 'gleam-popup';
      this.days = parseInt(this.dataset.days || '7', 10);
      this.delay = parseInt(this.dataset.delay || '10', 10) * 1000;
      this.exitIntent = this.dataset.exitIntent === 'true';
      this.querySelectorAll('[data-popup-close]').forEach(b => b.addEventListener('click', () => this.close(true)));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(true); });
      const form = this.querySelector('form');
      if (form) form.addEventListener('submit', () => this.remember());
      if (this.querySelector('.form-status--success')) { this.open(); return; }
      if (this.seen() || window.Shopify && window.Shopify.designMode) return;
      this.timer = setTimeout(() => this.open(), this.delay);
      if (this.exitIntent && window.matchMedia('(hover: hover)').matches) {
        document.addEventListener('mouseleave', (e) => { if (e.clientY <= 0) this.open(); }, { once: true });
      }
    }
    seen() { try { const t = localStorage.getItem(this.key); return t && (Date.now() - parseInt(t, 10)) < this.days * 86400000; } catch (e) { return false; } }
    remember() { try { localStorage.setItem(this.key, String(Date.now())); } catch (e) {} }
    open() {
      if (this.classList.contains('is-open') || document.querySelector('.cart-drawer.is-open, .drawer.is-open')) return;
      clearTimeout(this.timer);
      this.classList.add('is-open'); this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const input = this.querySelector('input[type="email"]'); if (input) setTimeout(() => input.focus(), 80);
    }
    close(remember) {
      this.classList.remove('is-open'); this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (remember) this.remember();
    }
  }
  if (!customElements.get('promo-popup')) customElements.define('promo-popup', PromoPopup);
  document.addEventListener('shopify:section:select', (e) => { const p = e.target.querySelector('promo-popup'); if (p) p.open(); });
  document.addEventListener('shopify:section:deselect', (e) => { const p = e.target.querySelector('promo-popup'); if (p) p.close(false); });
})();

/* ---------- Sticky bar: reserve space so the footer stays readable ---------- */
(() => {
  const sync = () => {
    const bar = document.querySelector('.sticky-atc');
    document.body.classList.toggle('has-sticky-atc', !!(bar && bar.classList.contains('is-visible')));
  };
  document.addEventListener('DOMContentLoaded', () => {
    const bar = document.querySelector('.sticky-atc');
    if (!bar) return;
    new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['class'] });
    sync();
  });
})();
